import React, { useState, useEffect, useRef } from 'react';
import CreateUser from './CreateUser';
import UndoRedoButtons from './UndoRedoButtons';
import DownloadOptions from './DownloadOptions';
import SearchFeature from './SearchFeature';
import ViewManager from './ViewManager/ViewManager.js';
import Email from './Email';
import FunctionBar from './FunctionBar';
import AssignFeature from './AssignFeature';
import ProjectsChoser from './ProjectsChoser';
import Admin from './Admin/Admin';
import AIPanel from './Admin/AISummaryPanel.js';
import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close'; // Import Close Icon
import ClearAllIcon from '@mui/icons-material/ClearAll';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import RowingIcon from '@mui/icons-material/Rowing';
import QuizIcon from '@mui/icons-material/Quiz';
import EmailIcon from '@mui/icons-material/Email';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import AISparkleIcon from "../../assets/AISparkleIcon.js";
import './DynamicIsland.css';
import TickerBand from './TickerBand.js';
import { PermissionType, ViewType } from '../../Enums';

const DynamicIsland = ({
  viewfieldsInstance,
  fields,
  rows,
  allSubmissions,
  logs,
  availableReports,
  availableMessages,
  reviewers,
  projectCode,
  onSearchUpdate,
  onAssign,
  onDelete,
  onAnalyse,
  isAnalysingSelected,
  onCreateUser,
  onMessageUpdate,
  onNewProject,
  onCellUpdate,
  onCycleComplete,
  onTickerClose,
  onColumnsChange,
  onViewsChange,
  onProjectsChange,
  clearTickerItems,
  selectedView,
  selectedRows,
  treeData,
  formData,
  cloneProjectTreeData,
  adminTokens,
  selectedProjectToken,
  selectedAccountToken,
  submissionToken,
  reviewingTokens,
  activeReviewToken,
  setActiveReviewToken,
  onAccountChange,
  onProjectChange,
  onViewChange,
  onSaveForm,
  onSaveAssets,
  dictionaryResolvers,
  formDictionaries,
  formPages,
  formLanguages,
  formName,
  formToken,
  currentProjectName,
  appUser,
  adminUsers,
  tickerItems,
  customTheme,
  dawnSnapshot,
}) => {
  const [isAssignOpen, setIsAssignOpen] = useState(false);
  const [isEmailOpen, setIsEmailOpen] = useState(false);
  const [isCreateUserOpen, setIsCreateUserOpen] = useState(false);
  const [isDownloadOptionsOpen, setIsDownloadOptionsOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isAdminOpen, setIsAdminOpen] = useState(false);
  const [isViewManagerOpen, setIsViewManagerOpen] = useState(false);
  const [isFunctionBarOpen, setIsFunctionBarOpen] = useState(selectedRows.length > 0);
  const [isReviewerListOpen, setIsReviewerListOpen] = useState(false);
  const [isProjectsOpen, setIsProjectsOpen] = useState(false);
  const [isTickerOpen, setIsTickerOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedReviewers, setSelectedReviewers] = useState([]);
  const [aiState, setAiState] = useState({ hasNewSummary: false, isProcessing: false, lastRead: null});
  const [isAiPanelOpen, setIsAiPanelOpen] = useState(false);
//console.log("DynamicIsland allSubmissions:", allSubmissions);
const testTickerItems = [
  "🆕 New user signup: Alice Johnson",
  "✅ Submission #123 approved",
  "⚠️ Warning: Disk usage above 90%",
  "✉️ New message from Bob",
  "🛠️ Deployment succeeded",
  "ℹ️ System maintenance scheduled at 02:00 UTC"
];

  // Effect to handle function bar visibility based on selectedRows or view type
  useEffect(() => {
  
    // Check if any other panel is open
    const anyOtherPanelOpen = 
      (isEmailOpen || isDownloadOptionsOpen || isSearchOpen ||
      isViewManagerOpen || isProjectsOpen || isAdminOpen) ;

    const isAnyFunctionPanelOpen = (isCreateUserOpen || isReviewerListOpen);

    // Reset everything if no rows are selected
    if (selectedRows.length === 0 && !anyOtherPanelOpen && !isAnyFunctionPanelOpen) {
      resetDynamicIsland();
    }  

    // Open the function bar only if conditions are met and no other panel is open
    if ((selectedRows.length > 0 || selectedView.type === ViewType.USERS) && !anyOtherPanelOpen) {
      setIsFunctionBarOpen(true);
    } else {
      setIsFunctionBarOpen(false);
    }
    
  }, [ selectedRows, selectedView, isEmailOpen,
    isDownloadOptionsOpen, isSearchOpen, isViewManagerOpen,
    isProjectsOpen, isAdminOpen, isFunctionBarOpen
  ]);

  // only auto–open when new items arrive (i.e. length increases)
  // sync visibility when items change
  useEffect(() => {
    if (tickerItems.length > 0) {
      setIsTickerOpen(true); // new items arrive → open
    } else {
      setIsTickerOpen(false); // emptied → hide
    }
  }, [tickerItems.length]);

  // Watch for Dawn snapshot changes
  useEffect(() => {
    if (!dawnSnapshot) return;
    
    try {
      const snapshot = typeof dawnSnapshot === 'string'
        ? JSON.parse(dawnSnapshot)
        : dawnSnapshot;
      
      const generatedAt = new Date(snapshot.lastSummaryGeneratedAt);
      const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);
      
      if (generatedAt > fiveMinutesAgo) {
        // New summary detected
        setAiState({
          hasNewSummary: true,
          isProcessing: false,
          lastRead: null,
        });
      }
    } catch (err) {
      console.error('Error parsing Dawn snapshot:', err);
    }
  }, [dawnSnapshot]);

  // Function to reset all the expansion states
  const resetDynamicIsland = () => {
    setIsAssignOpen(false);
    setIsEmailOpen(false);
    setIsCreateUserOpen(false);
    setIsDownloadOptionsOpen(false);
    setIsSearchOpen(false);
    setIsViewManagerOpen(false);
    setIsReviewerListOpen(false);
    setIsProjectsOpen(false);
    setIsFunctionBarOpen(false);
    setIsAdminOpen(false);
    setIsAiPanelOpen(false);
  };

  const getAiIconColor = () => {
    if (aiState.isProcessing) return '#FFD700'; // Gold - processing
    if (aiState.hasNewSummary) return '#A855F7'; // Purple - notification (matches icon's glow)
    return 'rgba(255, 255, 255, 0.7)'; // White/dim - idle
  };

  // Toggle AI panel
  const toggleAiPanel = () => {
    console.log("Toggling AI Panel. Currently open?", isAiPanelOpen);
    if (isAiPanelOpen) {
      setIsAiPanelOpen(false);
    } else {
      resetDynamicIsland();
      setIsAiPanelOpen(true);
      
      // Mark as read when opened
      if (aiState.hasNewSummary) {
        setAiState(prev => ({
          ...prev,
          hasNewSummary: false,
          lastRead: new Date(),
        }));
      }
    }
  };

  const toggleAdmin = () => {
    if (isAdminOpen) {
      setIsAdminOpen(false);
    } else {
      console.log("about to open Admin panel and close others");
      resetDynamicIsland();
      setIsAdminOpen(true);
    }
  };

  // Toggle functions with reset before expanding
  const toggleDownloadOptions = () => {
    if (isDownloadOptionsOpen) {
      setIsDownloadOptionsOpen(false);
    } else {
      resetDynamicIsland();
      setIsDownloadOptionsOpen(true);
    }
  };

  const toggleSearch = () => {
    if (isSearchOpen) {
      setIsSearchOpen(false);
    } else {
      resetDynamicIsland();
      setIsSearchOpen(true);
    }
  };

  const toggleViewManager = () => {
    if (isViewManagerOpen) {
      setIsViewManagerOpen(false);
    } else {
      resetDynamicIsland();
      setIsViewManagerOpen(true);
    }
  };

  const toggleEmail = () => {
    if (isEmailOpen) {
      setIsEmailOpen(false);
    } else {
      resetDynamicIsland();
      setIsEmailOpen(true);
    }
  };

  const toggleAssign = () => {
    setIsReviewerListOpen(!isReviewerListOpen);
  };

  const toggleProjects = () => {
    if (isProjectsOpen) {
      setIsProjectsOpen(false);
    } else {
      resetDynamicIsland();
      setIsProjectsOpen(true);
    }
  };

  const handleAssignClick = () => {
    setIsReviewerListOpen(true);
  };

  // Search functionality that updates the parent with the search term
  const handleSearch = (term) => {
    setSearchTerm(term);
    onSearchUpdate(term);
  };

  const clearSearch = () => {
    setSearchTerm('');
    onSearchUpdate('');
  };
  
  const handleAssignApplication = (selectedReviewers) => {
    onAssign(selectedReviewers);
    resetDynamicIsland();
  };

  const handleDeleteClick = () => {
    onDelete(selectedRows);
  };

  const handleMessageUpdate = (message) => {
    onMessageUpdate(message);
  };

  const handleViewChange = async (viewLabel) => {
    resetDynamicIsland();  
    onViewChange(viewLabel);  
  };

  // Dynamically apply multiple classes based on the state of the controls
  const dynamicIslandClasses = [
    'dynamic-island',
    isDownloadOptionsOpen ? 'download-expanded' : '',
    isSearchOpen ? 'search-expanded' : '',
    isViewManagerOpen ? 'customise-expanded' : '',
    isEmailOpen ? 'email-expanded' : '',
    isFunctionBarOpen ? 'function-bar-expanded' : '',
    isReviewerListOpen ? 'reviewer-list-expanded' : '',
    isCreateUserOpen ? 'create-user-expanded' : '',
    isProjectsOpen ? 'project-choice-expanded' : '',
    isTickerOpen ? 'ticker-band-expanded' : '',
    isAdminOpen ? 'admin-expanded' : '',
    isAiPanelOpen ? 'ai-panel-expanded' : '',
    ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className="island-wrapper">
      <div className={dynamicIslandClasses.trim()}>
      <UndoRedoButtons />

      {/* ticker strip – height toggles from 0 → N */}
      
      {/* ticker strip – height/opacity toggled by class, spans full island width */}
      <div className={`ticker-band-container ${isTickerOpen ? 'ticker-band-expanded' : ''}`}>
        <TickerBand
          items={tickerItems}
          onCycleComplete={onCycleComplete} // from grandparent
          onClose={onTickerClose}       // explicit user close
          speed={1}
        />
      </div>

      {/* AI Icon - Always visible, stateful */}
      <button
        className={`island-icon island-icon--ai${isAiPanelOpen ? ' icon-selected' : ''}${
          aiState.hasNewSummary ? ' island-icon--notification' : ''
        }${aiState.isProcessing ? ' island-icon--processing' : ''}`}
        onClick={toggleAiPanel}
      >
        <AISparkleIcon
          notification={true}
          sx={{ 
            fontSize: 40,
            color: getAiIconColor(),
            transition: 'color 0.3s ease',
          }}
        />
        {aiState.hasNewSummary && <div className="island-icon-badge" />}
      </button>

      {/* Admin */}
      <button
        className={`island-icon${isAdminOpen ? ' icon-selected' : ''}`}
        onClick={toggleAdmin}
      >
        {appUser.adminLevel === PermissionType.SUPER_ADMIN
          ? <WhatshotIcon sx={{ color: 'white' }} />
          : <AISparkleIcon fontSize="large" />}
      </button>

      {/* Projects Icon */}
      <button
        className={`island-icon${isProjectsOpen ? ' icon-selected' : ''}`}
         onClick={toggleProjects}
      >
        <ClearAllIcon sx={{ color: 'white' }} />
      </button>

      {/* Download Icon */}
      <button
        className={`island-icon${isDownloadOptionsOpen ? ' icon-selected' : ''}`}
        onClick={toggleDownloadOptions}
      >
        <DownloadIcon sx={{ color: 'white' }} />
      </button>

      {/* Customise View Icon */}
      <button
        className={`island-icon${isViewManagerOpen ? ' icon-selected' : ''}`}
         onClick={toggleViewManager}
         >
        <SettingsSuggestIcon sx={{ color: 'white' }} />
      </button>

      {/* Email Icon */}
      <button
        className={`island-icon${isEmailOpen ? ' icon-selected' : ''}`}
        onClick={toggleEmail}
      >
        <EmailIcon sx={{ color: 'white' }} />
      </button>

      {/* Search Icon */}
      <button
        className={`island-icon${isSearchOpen ? ' icon-selected' : ''}`}
        onClick={toggleSearch}
      >
        <SearchIcon sx={{ color: 'white' }} />
      </button>

      {/* Create User Form */}
      {isCreateUserOpen && <CreateUser onCreateUser={onCreateUser} />}
      
      {/* AI Panel */}
      {isAiPanelOpen && (
        <AIPanel
          snapshot={dawnSnapshot}
          isOpen={isAiPanelOpen}
        />
      )}

      {/* Admin Options */}
      {isAdminOpen && (
        <Admin
          viewfieldsInstance={viewfieldsInstance}
          allSubmissions={allSubmissions}
          dictionaryResolvers={dictionaryResolvers}
          formDictionaries={formDictionaries}
          formPages={formPages}
          formLanguages={formLanguages}
          formName={formName}
          formToken={formToken}
          projectHistoryLogs={logs}
          currentProjectName={currentProjectName}
          currentProjectToken={selectedProjectToken}
          submissionToken={submissionToken}
          reviewingTokens={reviewingTokens}
          activeReviewToken={activeReviewToken}
          setActiveReviewToken={setActiveReviewToken}
          appUser={appUser}
          adminUsers={adminUsers}
          adminTokens={adminTokens}
          onCreateUser={onCreateUser}
          onCellUpdate={onCellUpdate}
          isAdminOpen={isAdminOpen}
          treeData={cloneProjectTreeData}
          onNewProject={onNewProject}
          onSaveForm={onSaveForm}
          onSaveAssets={onSaveAssets}
          onProjectsChange={onProjectsChange}
          customTheme={customTheme}
        />
      )}

      {/* Projects Options */}
      {isProjectsOpen && (
        <ProjectsChoser
          selectedProjectToken={selectedProjectToken}
          selectedAccountToken={selectedAccountToken}
          treeData={treeData}
          selectedViewLabel={selectedView.viewLabel}          
          onAccountChange={onAccountChange}
          onProjectChange={onProjectChange}
          onViewChange={handleViewChange}
          customTheme={customTheme}
          isProjectsOpen={isProjectsOpen}
          currentUserId={appUser?.id}
        />
      )}

      {/* Download Options */}
      {isDownloadOptionsOpen && (
        <DownloadOptions
          viewfieldsInstance={viewfieldsInstance}
          fields={fields}
          rows={rows}
          allSubmissions={allSubmissions}
          submissionToken={submissionToken}
          reviewingTokens={reviewingTokens}
          availableReports={availableReports}
          projectCode={projectCode}
          formType={formData.type}
          viewType={selectedView.type}
          viewLabel={selectedView.viewLabel}
          selectedView={selectedView}
          appUser={appUser}
          isDownloadOptionsOpen={isDownloadOptionsOpen}
        />
      )}

      {/* Search Feature */}
      {isSearchOpen && (
        <SearchFeature
          isSearchOpen={isSearchOpen}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          handleSearch={onSearchUpdate}
        />
      )}

      {/* Customise View */}
      {isViewManagerOpen && (
        <ViewManager
          columns={fields}
          selectedViewLabel={selectedView.viewLabel}
          isCustomiseViewOpen={isViewManagerOpen}
          formData={formData}
          onColumnsChange={onColumnsChange}
          onViewsChange={onViewsChange}
          appUser={appUser}
          customTheme={customTheme}y
        />
      )}

      {/* Email */}
      {isEmailOpen && (
        <Email
        messages={availableMessages}
        allSubmissions={allSubmissions}
        onMessageUpdate={handleMessageUpdate}
        isEmailOpen={isEmailOpen}
        />
      )}

      {/* Function bar */}
      {isFunctionBarOpen && (
        <FunctionBar
          onAssign={() => setIsReviewerListOpen(true)}
          onDelete={handleDeleteClick}
          onAnalyse={onAnalyse}
          isAnalysing={isAnalysingSelected}
          selectedViewType={selectedView.type}
          onCreateUser={onCreateUser}
        />
      )}

      {/* Reviewer list */}
      {isReviewerListOpen && (
        <AssignFeature
          selectedRows={selectedRows}
          toggleAssign={toggleAssign}
          isAssignOpen={isReviewerListOpen}
          reviewers={reviewers}
          activeReviewToken={activeReviewToken}
          assignApplication={handleAssignApplication}
        />
      )}
      </div>
    </div>
  );
};

export default DynamicIsland;
